
import { computed, defineComponent, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  ColumnDefinition,
  getFilterParamAsStringArray,
  getFilterParams,
  OptionType,
  useDataTable,
  BaseButton,
  DeleteIcon,
  EditIcon,
  NorthIcon,
  SouthIcon,
  DataTable,
  FormFieldMultiSelect,
  CountButton,
  AdvancedSearch,
} from "@tager/admin-ui";
import {
  isNotNullish,
  notEmpty,
  useResourceDelete,
  useResourceMove,
} from "@tager/admin-services";
import { getNameWithDepth } from "@tager/admin-services";
import { Page } from "@tager/admin-layout";

import {
  deleteCategory,
  getCategoryList,
  moveCategory,
} from "@/services/requests";
import { CategoryType } from "@/typings/model";
import { getProductListUrl, getCategoryFormUrl } from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";

const typeOptionList = [
  { value: "CATALOG", label: "Разводящая" },
  { value: "PRODUCT", label: "Товарная" },
];

function typeTranslation(type: string): string {
  if (type === "PRODUCT") {
    return "Товарная";
  }
  if (type === "CATALOG") {
    return "Разводящая";
  }
  return "";
}

const COLUMN_DEFS: Array<ColumnDefinition<CategoryType>> = [
  {
    id: 1,
    name: "ID",
    field: "id",
    style: { width: "50px", textAlign: "center" },
    headStyle: { width: "50px", textAlign: "center" },
  },
  {
    id: 4,
    name: "Картинка",
    field: "image",
    type: "image",
    style: { width: "100px" },
    headStyle: { width: "100px" },
  },
  {
    id: 2,
    name: "Название",
    field: "name",
    type: "name",

    format: ({ row }) => ({
      adminLink: {
        text: getNameWithDepth(row.name, row.depth),
        url: getCategoryFormUrl({ categoryId: row.id }),
      },
      websiteLink: {
        text: getWebsiteOrigin() + row.url,
        url: getWebsiteOrigin() + row.url,
      },
      paramList: [
        row.type ? { name: "Тип", value: typeTranslation(row.type) } : null,
      ].filter(isNotNullish),
    }),
  },
  {
    id: 5,
    name: "",
    field: "products",
    style: { width: "198px", whiteSpace: "nowrap" },
    headStyle: { width: "198px" },
  },
  {
    id: 6,
    name: "Actions",
    field: "actions",
    style: { width: "177px", textAlign: "center", whiteSpace: "nowrap" },
    headStyle: { width: "177px", textAlign: "center" },
  },
];

interface TagType {
  value: string;
  label: string;
  name: string;
  title: string;
}

export default defineComponent({
  name: "CategoryList",
  components: {
    Page,
    DataTable,
    BaseButton,
    DeleteIcon,
    EditIcon,
    NorthIcon,
    SouthIcon,
    FormFieldMultiSelect,
    CountButton,
    AdvancedSearch,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const initialTypeFilter = computed<Array<OptionType>>(() => {
      const queryValue = getFilterParamAsStringArray(route.query, "type");

      return typeOptionList.filter((option) =>
        queryValue.some((selected) => option.value === selected)
      );
    });

    const typeFilter = ref<Array<OptionType>>(initialTypeFilter.value);

    watch(initialTypeFilter, () => {
      typeFilter.value = initialTypeFilter.value;
    });

    const filterParams = computed(() => {
      return getFilterParams({
        type: typeFilter.value.map((tag) => tag.value),
      });
    });

    const {
      isLoading: isCategoryListLoading,
      rowData: categoryList,
      errorMessage,
      searchQuery,
      handleChange,
      pageNumber,
      pageCount,
      pageSize,
      fetchEntityList: fetchCategoryList,
    } = useDataTable<CategoryType>({
      fetchEntityList: (params) =>
        getCategoryList({
          query: params.searchQuery,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          ...filterParams.value,
        }),
      initialValue: [],
      pageSize: 500,
      resourceName: "Category list",
    });

    watch(filterParams, () => {
      router.replace({
        query: {
          ...filterParams.value,
          query: (route.query.query ?? "") as string,
        },
      });

      fetchCategoryList();
    });

    const { handleResourceDelete: handleCategoryDelete, isDeleting } =
      useResourceDelete({
        deleteResource: deleteCategory,
        resourceName: "Category",
        onSuccess: fetchCategoryList,
      });

    const { handleResourceMove: handleCategoryMove, isMoving } =
      useResourceMove({
        moveResource: moveCategory,
        resourceName: "Category",
        onSuccess: fetchCategoryList,
      });

    const isRowDataLoading = computed<boolean>(
      () => isCategoryListLoading.value
    );

    function isBusy(categoryId: number): boolean {
      return (
        isDeleting(categoryId) || isMoving(categoryId) || isRowDataLoading.value
      );
    }

    function handleTagRemove(event: TagType) {
      if (event.name === "type") {
        typeFilter.value = typeFilter.value.filter(
          (tag) => tag.value !== event.value
        );
      }
    }

    const tags = computed<Array<TagType>>(() =>
      [
        ...typeFilter.value.map((tag) => ({
          value: tag.value,
          label: tag.label,
          name: "type",
          title: "Тип",
        })),
      ].filter(notEmpty)
    );

    return {
      columnDefs: COLUMN_DEFS,
      rowData: categoryList,
      isRowDataLoading,
      errorMessage,
      isBusy,
      handleCategoryDelete,
      handleCategoryMove,
      getCategoryFormUrl,
      getProductListUrl,
      searchQuery,
      handleChange,

      pageNumber,
      pageCount,
      pageSize,

      typeFilter,
      typeOptionList,

      handleTagRemove,
      tags,
    };
  },
});
